import { graphql } from 'react-relay';
import { useMemo } from 'react';

// oxlint-disable-next-line graphql-relay-compat/no-interop-imports -- see https://go.atlassian.com/cfe-no-interop-imports
import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';
import { expValEquals } from '@confluence/feature-experiments';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';

import { groupMeetings } from './groupMeetings.ts';
import { hasPremiumTier, type LicenseInformation } from './licenseInformation.ts';
import { mapLoomMeeting } from './mapLoomMeeting.ts';
import { useMeetingsLicenseInfo } from './useMeetingsLicenseInfo.ts';
import type {
	Meeting,
	MeetingDateRange,
	MeetingsEligibility,
	UseMeetingsResult,
} from './meetingTypes.ts';
import type {
	useMeetingsQuery as useMeetingsQuery$data,
	useMeetingsQueryVariables,
} from './__types__/useMeetingsQuery.ts';

/**
 * Live Relay-backed implementation of the Meetings data hook.
 *
 * This is the single seam between the UI and the data layer — every consuming
 * component depends only on the `UseMeetingsResult` contract, never on the query.
 *
 * Data sources (see tech spec https://hello.atlassian.net/wiki/spaces/Growth/pages/7345509583):
 * - `loom_recordedMeetings(siteId, rangeStart, rangeEnd)` → the user's recorded
 *   (notetaker) meetings, participation-based (any meeting on their connected
 *   calendars that was recorded, regardless of who recorded it). Returns a
 *   `{ edges { node } }` connection with a real server-side date range (7-day
 *   default), so no client-side date filtering is needed.
 * - `loom_settings(siteId)` → drives eligibility (TWC / connect-calendar / show).
 *
 * The AI recap ("Your recap") is sourced from `video.shortSummary`.
 *
 * One-click action items (M1CA) are read from `oneClickActions` nested on each
 * meeting and rendered by the swappable `ActionItem` boundary. The CTA redirect
 * recipes + acceptance mutation are a later layer (STARB-3845); this layer only
 * renders the items (pending + accepted; dismissed are filtered out).
 */

const MEETINGS_QUERY = graphql`
	query useMeetingsQuery(
		$cloudId: ID!
		$cloudIds: [ID!]!
		$siteAri: ID!
		$rangeStart: String
		$rangeEnd: String
	) {
		tenantContexts(cloudIds: $cloudIds) {
			confluenceEntitlementInfo: entitlementInfo(hamsProductKey: "confluence.ondemand") {
				... on CommerceEntitlementInfoCcp {
					entitlement {
						... on CcpEntitlement {
							relatesFromEntitlements {
								relationshipType
								entitlement {
									... on CcpEntitlement {
										offering {
											pricingType
										}
									}
								}
							}
							offering {
								pricingType
							}
						}
					}
				}
			}
			jiraEntitlementInfo: entitlementInfo(hamsProductKey: "jira-software.ondemand") {
				... on CommerceEntitlementInfoCcp {
					entitlement {
						... on CcpEntitlement {
							offering {
								pricingType
							}
						}
					}
				}
			}
		}
		loom_settings(siteId: $cloudId) {
			meetingRecordingsAvailable
		}
		loom_connectedCalendars(siteId: $cloudId) {
			guid
		}
		loom_recordedMeetings(
			siteId: $siteAri
			rangeStart: $rangeStart
			rangeEnd: $rangeEnd
			order: DESC
		) {
			edges {
				node {
					id
					title
					startsAt
					recorderId
					recorder {
						accountId
						name
						picture
					}
					inviteeAris
					invitees {
						accountId
						name
						picture
					}
					video {
						id
						url
						isMeeting
						meetingAri
						shortSummary
					}
					oneClickActions {
						id
						action
						type
						confluenceContentType
						status
						timestampSeconds
					}
					recurring
				}
			}
		}
	}
`;

export type UseMeetingsOptions = {
	/** Date range to fetch meetings for. Defaults to the server range (last 7 days). */
	dateRange?: MeetingDateRange;
	/** Whether the Meetings gate is on. When false the query is skipped. Defaults to true. */
	enabled?: boolean;
};

/**
 * Whether the site is a TWC customer, per the sanctioned Confluence pattern:
 * a Confluence entitlement that "relates from" a COLLECTION (bundle) relationship
 * indicates the site is part of a TWC collection. Mirrors
 * `useNthUserCrossjoinOnboardingEligible`.
 */
export const deriveHasTwc = (
	tenantContext: NonNullable<useMeetingsQuery$data['tenantContexts']>[number] | undefined,
): boolean => {
	const relatesFromEntitlements =
		tenantContext?.confluenceEntitlementInfo?.entitlement?.relatesFromEntitlements ?? [];
	return relatesFromEntitlements.some(
		(relationship) => relationship?.relationshipType === 'COLLECTION',
	);
};

/**
 * Whether the site has a **paid** TWC subscription.
 *
 * TWC is priced as a single bundle on CCP, so the individual products
 * (Confluence, Jira) can show `pricingType: FREE` even when the site pays for the
 * TWC bundle — the price lives on the parent bundle entitlement. We therefore
 * read the pricing from the COLLECTION relationship's related (bundle)
 * entitlement, not from the child Confluence entitlement.
 */
export const deriveHasTwcPaid = (
	tenantContext: NonNullable<useMeetingsQuery$data['tenantContexts']>[number] | undefined,
): boolean => {
	const relatesFromEntitlements =
		tenantContext?.confluenceEntitlementInfo?.entitlement?.relatesFromEntitlements ?? [];
	return relatesFromEntitlements.some(
		(relationship) =>
			relationship?.relationshipType === 'COLLECTION' &&
			relationship?.entitlement?.offering?.pricingType === 'PAID',
	);
};

export type ProductEntitlement = {
	entitlement?: { offering?: { pricingType?: string | null } | null } | null;
} | null;

/** Whether a product entitlement exists (site has the product provisioned). */
export const hasProduct = (info: ProductEntitlement | undefined): boolean =>
	info?.entitlement != null;

/** Whether a product entitlement is a paid plan (excludes FREE / LIMITED_FREE). */
export const isPaid = (info: ProductEntitlement | undefined): boolean =>
	info?.entitlement?.offering?.pricingType === 'PAID';

/**
 * Derive the eligibility state per the experiment targeting spec. The premium
 * tier (Loom Business + AI / TWC Premium) — read from license information — is
 * the primary split between co-use and cross-flow:
 *
 * 1. Co-use — site has the premium tier (`hasPremiumTier`: Loom Business + AI,
 *    which the full TWC Premium bundle implies). Sub-state:
 *      - `noConnectedCalendar` — no connected calendar OR recordings unavailable.
 *      - `eligible` — connected calendar and recordings available.
 * 2. `excluded` — site has TWC but is **not** premium tier. Paid or free, a
 *    non-premium TWC site is out of the experiment entirely: no pill, no banner.
 * 3. `noTwc` (cross-flow) — site is **not** premium tier and has **no** TWC, but
 *    is a paid Atlassian customer we want to cross-flow into TWC: Confluence paid
 *    OR Jira paid. (Confluence is always present on this surface.)
 * 4. `excluded` — everything else.
 *
 * Paid status still comes from the AGG `tenantContexts` query (`isPaid` /
 * `deriveHasTwc`); the license-information endpoint supplies **only** the
 * premium-tier signal.
 */
export const deriveEligibility = (
	data: useMeetingsQuery$data | undefined,
	license: LicenseInformation,
): Extract<MeetingsEligibility, 'eligible' | 'noTwc' | 'noConnectedCalendar' | 'excluded'> => {
	// Premium tier (Loom Business + AI / TWC Premium) → co-use, regardless of
	// whether the site has the TWC bundle. Checked first: a standalone Loom
	// Business + AI site (no TWC, no Jira) is co-use eligible.
	if (hasPremiumTier(license)) {
		const hasConnectedCalendar = (data?.loom_connectedCalendars ?? []).some(Boolean);
		// Require recordings to be explicitly available. `meetingRecordingsAvailable`
		// is `boolean | null`; treat null (unknown) the same as false so we don't
		// surface a dead-end "eligible" state when availability is unconfirmed.
		const recordingsAvailable = data?.loom_settings?.meetingRecordingsAvailable === true;
		if (!hasConnectedCalendar || !recordingsAvailable) {
			return 'noConnectedCalendar';
		}
		return 'eligible';
	}

	// Not premium tier. A site that already has TWC (paid or free) but isn't
	// premium is excluded — it's neither a co-use nor a cross-flow target.
	if (deriveHasTwc(data?.tenantContexts?.[0])) {
		return 'excluded';
	}

	// No TWC and not premium tier: cross-flow only for paid Atlassian customers.
	return isCrossFlowEligible(data) ? 'noTwc' : 'excluded';
};

/**
 * Whether a non-TWC site qualifies for the cross-flow experience: Confluence paid
 * OR Jira paid. Confluence is always present on this surface. Exported for
 * targeting/analytics use alongside the derived eligibility state.
 */
export const isCrossFlowEligible = (data: useMeetingsQuery$data | undefined): boolean => {
	const tenantContext = data?.tenantContexts?.[0];

	if (deriveHasTwc(tenantContext)) {
		return false;
	}

	return (
		isPaid(tenantContext?.confluenceEntitlementInfo) || isPaid(tenantContext?.jiraEntitlementInfo)
	);
};

// Gate off → treat as not eligible; still fetching (either the AGG query or the
// license-info request) → loading; else derive.
const deriveEligibilityState = (
	enabled: boolean,
	loading: boolean,
	data: useMeetingsQuery$data | undefined,
	license: LicenseInformation,
): MeetingsEligibility => {
	if (!enabled) {
		return 'excluded';
	}
	if (loading) {
		return 'loading';
	}
	return deriveEligibility(data, license);
};

/** Whether an eligibility state is one where the pill is allowed to show. */
const isPillVisibleState = (eligibility: MeetingsEligibility): boolean =>
	eligibility === 'eligible' || eligibility === 'noTwc' || eligibility === 'noConnectedCalendar';

/**
 * Shared query + eligibility derivation for the Meetings surface.
 *
 * This is the single place the `useMeetingsQuery` is issued. Both the
 * feed-level visibility hook and the panel-level data hook call this; because
 * the query uses identical variables + `cache-first`, only ONE network request
 * is made (the second call is a cache hit). It deliberately does NOT read the
 * experiment — exposure is owned solely by `useMeetingsVisibility`.
 */
const useMeetingsQueryData = (
	enabled: boolean,
	dateRange?: MeetingDateRange,
): {
	data: useMeetingsQuery$data | undefined;
	loading: boolean;
	error?: Error;
	eligibility: MeetingsEligibility;
} => {
	const { cloudId } = useSessionData();

	const rangeStart = dateRange?.rangeStart;
	const rangeEnd = dateRange?.rangeEnd;

	// Stable reference so the query isn't re-executed on every render. When no
	// range is supplied the args are omitted, so the server default window (7
	// days) applies — and the visibility + panel hooks share one cache entry.
	// loom_recordedMeetings resolves the Loom workspace via a site ARI, unlike
	// loom_settings/loom_connectedCalendars which accept the bare cloudId.
	const siteAri = `ari:cloud:loom::site/${cloudId}`;

	const variables = useMemo(
		() => ({ cloudId, cloudIds: [cloudId], siteAri, rangeStart, rangeEnd }),
		[cloudId, siteAri, rangeStart, rangeEnd],
	);

	const { data, loading, error } = useQueryInterop<
		useMeetingsQuery$data,
		useMeetingsQueryVariables
	>(MEETINGS_QUERY, {
		variables,
		errorPolicy: 'all',
		fetchPolicy: 'cache-first',
		// Gate off → skip the query (no request on the feed critical path).
		skip: !enabled,
	});

	// License information supplies the premium-tier targeting signal. Gated by
	// the same `enabled` flag so no extra request is made when the gate is off.
	const { loading: licenseLoading, info: license } = useMeetingsLicenseInfo(enabled);

	// Eligibility depends on both sources, so we're "loading" until both resolve.
	const combinedLoading = loading || licenseLoading;

	// Gate off → never fetching, so not "loading".
	const eligibility: MeetingsEligibility = deriveEligibilityState(
		enabled,
		combinedLoading,
		data,
		license,
	);

	return { data, loading: combinedLoading, error: error ?? undefined, eligibility };
};

/**
 * Feed-level visibility hook: gate → targeting → flag.
 *
 * Owns the SINGLE experiment exposure. Called once from `Feed` to decide whether
 * to render the Meetings pill. Exposure fires for every targeted user (control
 * AND treatment) so the experiment has both cohorts; `shouldShow` is true only
 * for treatment, so only treatment users actually see the pill.
 */
export const useMeetingsVisibility = (
	options: { enabled?: boolean } = {},
): { shouldShow: boolean } => {
	const { enabled = true } = options;
	const { eligibility } = useMeetingsQueryData(enabled);

	// Read the experiment only once targeting passes, so exposure fires for the
	// targeted cohort only. `excluded`/`loading` never expose and never show.
	const shouldShow =
		isPillVisibleState(eligibility) &&
		expValEquals('meetings_tab_in_confluence_for_you_page', 'cohort', 'variant');

	return { shouldShow };
};

/**
 * Panel-level data hook: meetings list + grouping for the rendered panel.
 *
 * Consumes the SAME cached query as `useMeetingsVisibility` (no extra network
 * request) and does NOT read the experiment (no duplicate exposure). Only
 * mounted for treatment users, since the feed gates the pill on `shouldShow`.
 */
export const useMeetings = (options: UseMeetingsOptions = {}): UseMeetingsResult => {
	const { dateRange, enabled = true } = options;

	const { data, loading, error, eligibility } = useMeetingsQueryData(enabled, dateRange);

	const groups = useMemo(() => {
		if (loading || eligibility !== 'eligible') {
			return [];
		}

		// `loom_recordedMeetings` already scopes to the user's recorded meetings
		// within the requested (server-side) date range, so no participant/date
		// filtering is needed here — just keep meetings with a playable recording.
		const edges = data?.loom_recordedMeetings?.edges ?? [];
		const meetings: Meeting[] = [];
		for (const edge of edges) {
			const node = edge?.node;
			if (!node?.video?.url) {
				continue;
			}
			meetings.push(mapLoomMeeting(node));
		}
		meetings.sort((a, b) => Date.parse(b.startsAt) - Date.parse(a.startsAt));

		return groupMeetings(meetings);
	}, [loading, eligibility, data]);

	const totalCount = useMemo(
		() => groups.reduce((sum, group) => sum + group.meetings.length, 0),
		[groups],
	);

	return {
		eligibility,
		loading,
		error,
		groups,
		totalCount,
		// Visibility/exposure is owned by `useMeetingsVisibility` at the feed
		// level; the panel is only mounted for treatment users. Kept in the
		// contract for back-compat; the panel does not gate on it.
		shouldShow: isPillVisibleState(eligibility),
	};
};
